// lib/auth.js — client session layer for magic-link sign-in (Auth Phase 1).
// The backend issues a 30-day signed session from POST /auth/verify. We store it
// and send it as X-WTR-Auth on API calls. Phase 2 wired actingEmail() server-side
// so the session is trusted over the client email; Phase 3 makes the session
// REQUIRED on identity routes. installAuthGuard() (below) catches the 401 that a
// session-less or expired request now gets, clears the dead session once, and
// routes the user to sign in, so an expired token is one re-sign-in, not a wall
// of broken screens.
import { API_BASE, SERVICE_TOKEN } from './apiConfig';
const SESSION_KEY = 'wtr_session'; // { token, email, ts }
export function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null'); } catch (e) { return null; }
}
export function isSignedIn() {
  const s = getSession();
  return !!(s && s.token && s.email);
}
export function sessionEmail() {
  const s = getSession();
  return s && s.email ? s.email : null;
}
export function setSession(token, email) {
  const e = String(email || '').trim().toLowerCase();
  try { localStorage.setItem(SESSION_KEY, JSON.stringify({ token, email: e, ts: Date.now() })); } catch (x) {}
  // Keep wtr_profile.email in sync so every existing screen reads this identity.
  try {
    const p = JSON.parse(localStorage.getItem('wtr_profile') || '{}');
    p.email = e;
    localStorage.setItem('wtr_profile', JSON.stringify(p));
  } catch (x) {}
}
export function clearSession() {
  try { localStorage.removeItem(SESSION_KEY); } catch (e) {}
}
// Headers for API calls: legacy service token plus the session token when present.
// Screens adopt this in Phase 2; sending X-WTR-Auth early is a no-op server-side
// until actingEmail() is wired, so it cannot break anything now.
export function authHeaders(extra) {
  const h = Object.assign(
    { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SERVICE_TOKEN}` },
    extra || {}
  );
  const s = getSession();
  if (s && s.token) h['X-WTR-Auth'] = s.token;
  return h;
}
// Ask the backend to email a sign-in link.
export async function requestLink(email) {
  const r = await fetch(`${API_BASE}/api/wtr/auth/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SERVICE_TOKEN}` },
    body: JSON.stringify({ email: String(email || '').trim().toLowerCase() }),
  });
  let d = {};
  try { d = await r.json(); } catch (e) {}
  if (!r.ok || !d.ok) throw new Error(d.error || 'Could not send the sign-in email. Please try again.');
  return true;
}
// On app load: if the URL carries ?auth_token=..., exchange it for a session,
// store it, and strip it from the URL so it never lingers in history.
// Returns { email } on success, { error } on a bad/expired link, or null when
// there was no token to consume.
export async function consumeAuthTokenFromUrl() {
  let token = null;
  try {
    const url = new URL(window.location.href);
    token = url.searchParams.get('auth_token');
    if (!token) return null;
  } catch (e) { return null; }
  let result;
  try {
    const r = await fetch(`${API_BASE}/api/wtr/auth/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SERVICE_TOKEN}` },
      body: JSON.stringify({ token }),
    });
    let d = {};
    try { d = await r.json(); } catch (e) {}
    if (r.ok && d.ok && d.token && d.email) {
      setSession(d.token, d.email);
      result = { email: d.email };
    } else {
      result = { error: (d && d.error) || 'This sign-in link is invalid or expired.' };
    }
  } catch (e) {
    result = { error: 'Sign-in could not be completed. Please try again.' };
  }
  // Always strip auth_token from the visible URL, success or not.
  try {
    const u = new URL(window.location.href);
    u.searchParams.delete('auth_token');
    window.history.replaceState({}, '', u.pathname + (u.search || '') + (u.hash || ''));
  } catch (e) {}
  return result;
}

// Phase 3 guard. Installed once at app boot. Wraps window.fetch and watches for a
// 401 from the Sales Engine, which after the Phase 3 cutover means "session
// required and missing or expired." When it sees one, it clears the dead session
// and fires a single 'wtr-auth-expired' event; the app shell listens and opens the
// sign-in screen. Deliberately scoped: it only reacts to 401s whose URL begins with
// our API base, and it excludes the /api/wtr/auth/ endpoints so a bad magic link
// (a 401 from verify) can never nuke an otherwise-fine state or loop. It reads only
// res.status, never the body, so the caller's res.json() is untouched. The cleaner
// long-term shape is a per-call apiFetch wrapper, but this avoids re-touching every
// screen's fetch site again.
let _authGuardInstalled = false;
export function installAuthGuard() {
  if (_authGuardInstalled) return;
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  _authGuardInstalled = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const res = await orig(input, init);
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (res && res.status === 401 &&
          url.indexOf(API_BASE) === 0 &&
          url.indexOf('/api/wtr/auth/') === -1) {
        clearSession();
        try { window.dispatchEvent(new CustomEvent('wtr-auth-expired')); } catch (e) {}
      }
    } catch (e) { /* never let the guard interfere with the request */ }
    return res;
  };
}
